package er.prateek.crossly.ui.screens.crossword

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInRoot
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.ui.theme.game
import kotlin.math.PI
import kotlin.math.roundToInt
import kotlin.math.sin

/** A letter currently under the player's finger, positioned in root coordinates. */
data class DraggedLetter(val letter: Char, val position: Offset)

/** A letter on its way back to the rack after being taken off the board. */
class LetterFlight(val id: Long, val letter: Char, val from: Offset, val to: Offset)

/**
 * Shared state for moving letters between the rack and the board by hand.
 *
 * Both gestures need the same thing: the on-screen position of every square and
 * every rack tile, in one coordinate space. Composables report their bounds here
 * as they are laid out, and the drag ghost and the fly-back animation are drawn
 * from a single overlay on top of the screen — no letter can be clipped by the
 * board frame or the rack it is travelling between.
 *
 * The bounds registries are deliberately plain maps rather than snapshot state:
 * they are written from layout and read during composition, and making them
 * observable would turn every layout pass into another recomposition.
 */
@Stable
class LetterDragState {
    private val cellBounds = HashMap<CellPos, Rect>()
    private val tileBounds = HashMap<Char, Rect>()
    private var rackBounds: Rect? = null

    var dragged: DraggedLetter? by mutableStateOf(null)
        private set

    val flights = mutableStateListOf<LetterFlight>()
    private var nextFlightId = 0L

    // --- what's where -------------------------------------------------------

    fun registerCell(pos: CellPos, bounds: Rect) {
        cellBounds[pos] = bounds
    }

    fun forgetCell(pos: CellPos) {
        cellBounds.remove(pos)
    }

    fun boundsOf(pos: CellPos): Rect? = cellBounds[pos]

    /** Only tiles that can still be played register — they are the flight targets. */
    fun registerTile(letter: Char, bounds: Rect) {
        tileBounds[letter] = bounds
    }

    fun forgetTile(letter: Char, bounds: Rect) {
        if (tileBounds[letter] == bounds) tileBounds.remove(letter)
    }

    fun registerRack(bounds: Rect) {
        rackBounds = bounds
    }

    // --- dragging -----------------------------------------------------------

    fun startDrag(letter: Char, at: Offset) {
        dragged = DraggedLetter(letter, at)
    }

    fun moveDrag(by: Offset) {
        dragged = dragged?.let { it.copy(position = it.position + by) }
    }

    /** The square the finger is over, if a letter may legally be dropped there. */
    val hoveredCell: CellPos?
        get() = dragged?.let { cellUnder(it.position) }

    /** Ends the drag and reports where it landed. */
    fun endDrag(): CellPos? {
        val target = hoveredCell
        dragged = null
        return target
    }

    fun cancelDrag() {
        dragged = null
    }

    private fun cellUnder(position: Offset): CellPos? =
        cellBounds.entries.firstOrNull { (_, bounds) -> bounds.contains(position) }?.key

    /**
     * Sends a letter taken off the board back down to the rack.
     *
     * It aims for a tile showing that letter, falls back to the rack as a whole,
     * and — with the full keyboard, where there is no rack to return to — simply
     * drops away below the square it came from.
     */
    fun flyToRack(letter: Char, from: Rect) {
        val destination = tileBounds[letter]?.center
            ?: rackBounds?.center
            ?: from.center + Offset(0f, from.height * 3f)
        flights += LetterFlight(nextFlightId++, letter, from.center, destination)
    }

    fun endFlight(id: Long) {
        flights.removeAll { it.id == id }
    }
}

@Composable
fun rememberLetterDragState(): LetterDragState = remember { LetterDragState() }

/**
 * Makes a key draggable onto the board.
 *
 * Tapping still types into the cursor square the way it always has: the tap
 * detector in the key's own `clickable` gives up as soon as this one consumes
 * movement past the touch slop, so a tap and a drag can share the same key
 * without either having to be chosen up front.
 *
 * Returns [Modifier] unchanged when there is nothing to drag with (no state, or
 * a spent tile), so a disabled key stays inert.
 */
@Composable
fun Modifier.draggableLetter(
    letter: Char,
    state: LetterDragState?,
    enabled: Boolean = true,
    onDropped: (CellPos, Char) -> Unit,
): Modifier {
    if (state == null || !enabled) return this

    // Where the key sits, so the ghost can start under the finger rather than
    // jumping to the top-left of the screen.
    var origin by remember { mutableStateOf(Offset.Zero) }
    return this
        .onGloballyPositioned { origin = it.positionInRoot() }
        .pointerInput(letter, state, onDropped) {
            detectDragGestures(
                onDragStart = { offset -> state.startDrag(letter, origin + offset) },
                onDrag = { change, amount ->
                    change.consume()
                    state.moveDrag(amount)
                },
                onDragEnd = { state.endDrag()?.let { target -> onDropped(target, letter) } },
                onDragCancel = { state.cancelDrag() },
            )
        }
}

/** Reports a rack tile's position so a letter coming off the board can aim for it. */
@Composable
fun Modifier.rackTileBounds(letter: Char, state: LetterDragState?, enabled: Boolean = true): Modifier {
    if (state == null || !enabled) return this
    var reported by remember { mutableStateOf(Rect.Zero) }
    DisposableEffect(state, letter) {
        onDispose { state.forgetTile(letter, reported) }
    }
    return this.onGloballyPositioned {
        reported = it.boundsInRoot()
        state.registerTile(letter, reported)
    }
}

/**
 * Draws whatever is in mid-air: the tile under the player's finger, and any
 * letters flying back to the rack.
 *
 * Sits last in the screen's root [Box] so it paints over the board and the rack,
 * and takes no pointer input of its own, so the gestures underneath still work.
 */
@Composable
fun LetterDragOverlay(
    state: LetterDragState,
    tileSize: Dp,
    modifier: Modifier = Modifier,
) {
    // Everything is tracked in root coordinates; the overlay subtracts its own
    // origin to place children within itself.
    var origin by remember { mutableStateOf(Offset.Zero) }

    Box(
        modifier = modifier
            .fillMaxSize()
            .onGloballyPositioned { origin = it.positionInRoot() },
    ) {
        state.dragged?.let { dragged ->
            // Carried above the finger rather than under it: the square being
            // targeted has to stay visible, and on a phone the hand is already
            // covering everything below the touch point.
            val carry = with(LocalDensity.current) { (tileSize * 0.85f).toPx() }
            FloatingLetter(
                letter = dragged.letter,
                center = dragged.position - origin - Offset(0f, carry),
                size = tileSize * 1.15f,
                alpha = 0.95f,
            )
        }
        state.flights.forEach { flight ->
            FlyingLetter(
                flight = flight,
                origin = origin,
                size = tileSize,
                onLanded = { state.endFlight(flight.id) },
            )
        }
    }
}

@Composable
private fun FlyingLetter(
    flight: LetterFlight,
    origin: Offset,
    size: Dp,
    onLanded: () -> Unit,
) {
    val progress = remember(flight.id) { Animatable(0f) }
    LaunchedEffect(flight.id) {
        progress.animateTo(1f, tween(durationMillis = 360, easing = FastOutSlowInEasing))
        onLanded()
    }

    val p = progress.value
    val lift = with(LocalDensity.current) { 26.dp.toPx() }
    // A shallow arc rather than a straight line: the letter reads as tossed back
    // to the rack instead of sliding under the board.
    val center = Offset(
        x = flight.from.x + (flight.to.x - flight.from.x) * p,
        y = flight.from.y + (flight.to.y - flight.from.y) * p - sin(p * PI).toFloat() * lift,
    )

    FloatingLetter(
        letter = flight.letter,
        center = center - origin,
        size = size * (1f - 0.25f * p),
        alpha = 1f - 0.55f * p,
    )
}

@Composable
private fun FloatingLetter(
    letter: Char,
    center: Offset,
    size: Dp,
    alpha: Float,
) {
    val colors = MaterialTheme.game
    val shape = RoundedCornerShape(10.dp)
    val half = with(LocalDensity.current) { size.toPx() / 2f }

    Box(
        modifier = Modifier
            .offset { IntOffset((center.x - half).roundToInt(), (center.y - half).roundToInt()) }
            .width(size)
            .height(size)
            .graphicsLayer { this.alpha = alpha }
            .clip(shape)
            .background(colors.cellFocus)
            .border(2.dp, colors.accent, shape),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = letter.toString(),
            fontSize = (size.value * 0.46f).coerceIn(14f, 26f).sp,
            fontWeight = FontWeight.Bold,
            color = colors.cellText,
        )
    }
}
